import { EntitiesStore } from './entities-store.abstract';
import { HttpClient, HttpParams } from '@angular/common/http';
import { tap, mergeMap, filter, map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { EventGeneralInfoItem } from 'world-archery-lib';
import { WaHelperService } from '../../../library/wa-services/wa-helper-service';
import { CountryDependencyEntityStore } from './country-dependency-entity-store';
import { Injector } from '@angular/core';
import { CategoryCombo } from '../filters-store/filters-store';
import { IAthleteEditorialBiography } from './athlete-biography-store';

/**
 * Interface for events store
 *
 * @export
 * @interface IEvents
 */
export interface IEvents {
  ID: number;
  Name: string;
  NameShort: string;
  Venue: string;
  Place: string;
  Country: string;
  CountryName: string;
  DFrom: string;
  DTo: string;
  WithRes: boolean;
  ComEventType: number;
  ComLevel: number;
  ComSubLevel: number;
  ComDis: number;
  ComDivisions: number[];
  EventType: string;
  Level: string;
  SubLevel: string;
  IsFeatured: boolean;
  WorldRecordStatus: boolean;
  WorldRankingEvent: boolean;
  IsLive: boolean;
  IsCancelled: boolean;
  ComHideInLive: string;
  Options: string[];
  CatInd: any[];
  CatTeam: any[];
  Status: string;
  Stages: any[];
  YoutubeLive: string;
  Links: Links;
  Logo: Logo;
}
interface Links {
  Web: string;
}
interface Logo {
  URL: string;
  MimeType: string;
}

/**
 * Competition store
 *
 * @export
 * @class CompetitionStore
 * @extends {EntitiesStore<IEvents>}
 */
export class CompetitionStore extends EntitiesStore<IEvents> {
  constructor(http: HttpClient, injector: Injector) {
    super('COMPETITIONS', http, [
      { entity: new CountryDependencyEntityStore(http, injector) },
      { entity: new CategoryCombo('', '', http), params: { 'RBP': 'All' } },
      { entity: new CompetitionStatusStore(http, injector) },
      { entity: new CompetitionLevelsStore(http, injector), params: { 'RBP': 'All' } },
      { entity: new DivisionsStore(http, injector), params: { 'RBP': 'All' } }
    ]);

  }

  /**
   * Method for get one object of division using the id.
   *
   * @param {string} id The id to be found.
   * @returns the division object or undefined.
   * @memberof CompetitionStore
   */
  getDivisionById(id: string): Partial<IDivision> {
    const dependency = this.dependencies[4].entity as DivisionsStore;
    const finded = dependency.data.find(value => value.Id.toString() == id.toString());
    return finded ? finded : undefined;
  }


  /**
   * Method for get one object of level using the id.
   *
   * @param {string} id The id to be found.
   * @returns the competition object or undefined.
   * @memberof CompetitionStore
   */
  getLevelById(id: string): ICompetitionLevel {
    const dependency = this.dependencies[3].entity as CompetitionLevelsStore;
    const finded = dependency.data.find(value => value.Id.toString() == id.toString());
    return finded ? finded : undefined;
  }

  /**
   * Method for get the competitions categories that belongs to a competition.
   *
   * @memberof CompetitionStore
   */
  getCompetitionCategories() {
    return this.dataStream.pipe(
      filter(a => a !== undefined && a.length > 0),
      map(a => a[0].CatInd),
      map(a => {
        return this.dependencies[1].entity.data.filter(category => a.findIndex(value => value.Code === category.Code) > -1)
      }),
      tap(a => console.log(a, this)),

    )
  }

  /**
 * Method for get the competitions team categories that belongs to a competition.
 *
 * @memberof CompetitionStore
 */
  getCompetitionTeamCategories() {
    return this.dataStream.pipe(
      filter(a => a !== undefined && a.length > 0),
      map(a => a[0].CatTeam),
      map(a => {
        return this.dependencies[1].entity.data.filter(category => a.findIndex(value => value.Code === category.Code) > -1)
      }),
      tap(a => console.log(a, this)),

    )
  }
}

export interface ICompetitionStatus {
  StatusId: number;
  Description: string;
  CompetitionScheduled: boolean;
}


/**
 * Competition status store
 *
 * @export
 * @class CompetitionStore
 * @extends {EntitiesStore<ICompetitionStatus>}
 */
export class CompetitionStatusStore extends EntitiesStore<ICompetitionStatus> {
  constructor(http: HttpClient, injector: Injector) {
    super('COMPETITIONSTATUS', http);

  }

}

/**
 * Interface for division store.
 *
 * @export
 * @interface IDivision
 */
export interface IDivision {
  Id: number;
  Code: string;
  Name: string;
  LimitedToDiscipline: number[];
}


/**
 * Divisions store
 *
 * @export
 * @class CompetitionStore
 * @extends {EntitiesStore<ICompetitionStatus>}
 */
export class DivisionsStore extends EntitiesStore<IDivision> {
  constructor(http: HttpClient, injector: Injector) {
    super('DIVISIONS', http);

  }

}


/**
 * Interface for subLevels in competition.
 *
 * @export
 * @interface SubLevel
 */
export interface ISubLevel {
  SubId: string;
  SubLevel: string;
  SubViewOrder: string;
}

/**
 * Interface for competition levels.
 *
 * @export
 * @interface ICompetitionLevel
 */
export interface ICompetitionLevel {
  Id: string;
  Level: string;
  ViewOrder: string;
  SubLevels: ISubLevel[];
}

/**
 * Competition levels store.
 *
 * @export
 * @class CompetitionStore
 * @extends {EntitiesStore<ICompetitionStatus>}
 */
export class CompetitionLevelsStore extends EntitiesStore<ICompetitionLevel> {
  constructor(http: HttpClient, injector: Injector) {
    super('COMPETITIONLEVELS', http);
  }
}


export interface Athlete {
  Id: string;
  FName: string;
  GName: string;
  WNameOrd: boolean;
  NOC: string;
}

export interface Result {
  Rnk: number;
  Athlete: Athlete;
}

export interface IndividualRankingInterface {
  Code: string;
  Results: Result[];
}


/**
 * Competition status store
 *
 * @export
 * @class CompetitionStore
 * @extends {EntitiesStore<IÇompetitionStatus>}
 */
export class CompetitionIndividualRankingsStore extends EntitiesStore<IndividualRankingInterface> {
  constructor(http: HttpClient, injector: Injector) {
    super('INDIVIDUALRANKINGS', http, [{ entity: new CategoryCombo('', '', http), params: { RBP: "ALL" } },
    ]);
  }

}

export interface ITeamCompetitionRankingMember {
  Id: string;
  FName: string;
  GName: string;
  WNameOrd: boolean;
}

export interface ICompetitionRankingTeamResult {
  Rnk: number;
  NOC: string;
  Name: string;
  Members: ITeamCompetitionRankingMember[];
}

export interface ITeamCompetitionRanking {
  Code: string;
  Results: Result[];
}


/**
 * Competition status store
 *
 * @export
 * @class CompetitionStore
 * @extends {EntitiesStore<IÇompetitionStatus>}
 */
export class CompetitionTeamRankingsStore extends EntitiesStore<ITeamCompetitionRanking> {
  constructor(http: HttpClient, injector: Injector) {
    super('TEAMRANKINGS', http, [{ entity: new CategoryCombo('', '', http), params: { RBP: "ALL" } },
    ]);
  }

}

/**
 * The editorial information interface for the competition.
 *
 * @export
 * @interface ICompetitionEditorialInformation
 */
export interface ICompetitionEditorialInformation {
  // athlete: IAthlete;
  // competition: IEditorialCompetition;
  author: string;
  date: string;
  body: string;
  publish_date: string;
}


/**
 * The store for the Drupal API that contains WYSIWYG information about the competition.
 *
 * @export
 * @class AthleteEditorialBiographyStore
 * @extends {EntitiesStore<ICompetitionEditorialInformation>}
 */
export class CompetitionInformationStore extends EntitiesStore<ICompetitionEditorialInformation> {
  constructor(http: HttpClient, injector: Injector) {
    super(null, http, [{ entity: new CountryDependencyEntityStore(http, injector), params: { 'RBP': 'ALL' } }]);
  }

  /**
   * {@inheritdoc}
   *
   * @memberof CompetitionInformationStore
   */
  getRequest(params: any, request_more: boolean) {
    // tslint:disable-next-line:max-line-length
    return this.http.get<ICompetitionEditorialInformation[]>(environment.competition.editorial_information.replace('competition_id', params['competition_id'].toString()))
      .pipe(
        tap((a: any) => {
          this.data = a;
          this.dataStream.next(this.data);
        })
      ) as any;
  }
}



/**
 * The store for the Drupal API that contains WYSIWYG information about the quote for a competition.
 *
 * @export
 * @class QuotesCompetitionStore
 * @extends {EntitiesStore<IAthleteEditorialBiography>}
 */
export class QuotesCompetitionStore extends EntitiesStore<IAthleteEditorialBiography> {

  /**
   * Creates an instance of QuotesCompetitionStore.
   * @param {HttpClient} http The http client from Angular.
   * @param {Injector} injector The injector from Angular.
   * @param {string} competition_id The athlete id.
   * @memberof QuotesCompetitionStore
   */
  constructor(http: HttpClient, injector: Injector, public competition_id: string) {
    super(null, http, [{ entity: new CountryDependencyEntityStore(http, injector), params: { 'RBP': 'ALL' } }]);
  }

  /**
   * {@inheritdoc}
   *
   * @memberof QuotesCompetitionStore
   */
  getRequest(params: any, request_more: boolean) {
    // tslint:disable-next-line:max-line-length
    return this.http.get<IAthleteEditorialBiography[]>(environment.editorial_quotes,
       { params: Object.assign(params, { competition_id: this.competition_id }) }
    )
      .pipe(
        tap((a: any) => {
          this.data = a;
          this.dataStream.next(this.data);
        })
      ) as any;
  }
}
